import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Switch from 'components/form/Switch';
import Button from 'components/Button';
import Select from 'components/form/Select';
import SMSComposer from 'modules/messages/components/SMSComposer';
import { snack } from 'utils/toast';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useConnectionOptions from 'hooks/useConnectionOptions';
import { htmlToText } from 'components/form/AdvanceTextEditor';

const GroupFormCompletionSettings = ({ group, onUpdateGroup }) => {
  const [t] = useTranslation('groups');
  const [msg, setMessage] = useState(group.group_form_completion_sms);
  const [attachment, setAttachment] = useState(
    group?.attachment?.length === 0 ? '' : group?.attachment,
  );
  const [enabled, setEnabled] = useState(
    group.group_form_completion_sms_enabled === '1',
  );
  const [connectionId, setConnectionId] = useState(
    group.group_form_completion_sms_connection_id,
  );
  const [expanded, setExpanded] = useState(false);
  const user = useSelector((state) => state.user.user);

  const connectionOptions = useConnectionOptions();

  // todo media group_reply_media_url
  // connection group_form_completion_sms_connection_id

  const onToggle = (e) => {
    const { checked } = e.target;
    setEnabled(checked);
    return onUpdateGroup({
      group_form_completion_sms_enabled: checked ? '1' : '0',
    })
      .then(() => {
        if (checked) {
          setExpanded(checked);
        }
      })
      .catch((e) => {
        setEnabled(!checked);
        snack.error(e.message);
      });
  };

  const onSave = async () => {
    const text = htmlToText(msg);
    try {
      if (!text) {
        throw new Error('Please enter a message');
      } else if (!connectionId || connectionId === '0') {
        throw new Error(t('group-form-completion-sms-conn-id-required'));
      }
      await onUpdateGroup({
        group_form_completion_sms: text,
        group_form_completion_sms_connection_id: connectionId,
        group_reply_file_id: attachment?.file_id ?? null,
      });
      snack.success('Message saved');
    } catch (e) {
      snack.error(e.message);
    }
  };

  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion expanded={expanded} elevation={6}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row">
            <Switch margin="none" checked={enabled} onChange={onToggle} />
            <Box onClick={toggleExpanded}>
              <Typography gutterBottom>
                {t(
                  'send-confirmation-text-message-heading',
                  'Send Confirmation Text Message',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'send-confirmation-text-message-desc',
                  'Send an immediate text message to the person after they complete the connect card.',
                )}
              </Typography>
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box my={2}>
            <Select
              transformValue
              margin="none"
              label={t('group-form-completion-conn-id-label')}
              value={connectionId}
              onChange={setConnectionId}
              options={connectionOptions}
              formatOptionLabel={(option, { context }) =>
                context === 'value' ? option.address : option.label
              }
            />
            <SMSComposer
              message={msg}
              onChange={setMessage}
              attachment={attachment}
              onAttachmentSelect={setAttachment}
            />
            <Box textAlign="right">
              <Button onClick={onSave}>{t('common:save', 'Save')}</Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default GroupFormCompletionSettings;
